import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/upload';
import { BED_TYPES, ROOM_AMENITIES } from '@/lib/rooms';

export interface RoomRecord {
  id?: string;
  business_id?: string | null;
  owner_id?: string;
  name: string;
  description: string;
  price_per_night: number;
  capacity: number;
  bed_type: string;
  images: string[];
  amenities: string[];
  available: boolean;
}

interface RoomFormProps {
  businessId: string | null;
  ownerId: string;
  existing?: RoomRecord | null;
  onSaved: () => void;
}

function emptyForm(): RoomRecord {
  return {
    name: '',
    description: '',
    price_per_night: 0,
    capacity: 2,
    bed_type: 'Lit double',
    images: [],
    amenities: [],
    available: true,
  };
}

function RoomForm({ businessId, ownerId, existing, onSaved }: RoomFormProps) {
  const [form, setForm] = useState<RoomRecord>(() => (existing ? { ...existing } : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);

  const update = (field: keyof RoomRecord, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const toggleAmenity = (amenity: string) => {
    setForm((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !businessId) return;
    setUploading(true);
    setErrorMsg('');
    try {
      const urls: string[] = [];
      for (let i = 0; i < files.length; i += 1) {
        const url = await uploadFile(files[i], `rooms/${businessId}`);
        urls.push(url);
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Échec de l'upload des images.");
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setStatus('error');
      setErrorMsg('Le nom de la chambre est obligatoire.');
      return;
    }
    if (!form.price_per_night || Number(form.price_per_night) <= 0) {
      setStatus('error');
      setErrorMsg('Le prix par nuit doit être supérieur à zéro.');
      return;
    }

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      price_per_night: Number(form.price_per_night) || 0,
      capacity: Number(form.capacity) || 1,
      bed_type: form.bed_type,
      images: form.images,
      amenities: form.amenities,
      available: form.available,
    };

    let error = null;
    if (existing?.id) {
      const res = await supabase.from('hotel_rooms').update(payload).eq('id', existing.id);
      error = res.error;
    } else {
      const res = await supabase.from('hotel_rooms').insert({
        ...payload,
        business_id: businessId,
        owner_id: ownerId,
      });
      error = res.error;
    }

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-hotel-bed-line text-primary-400" />
          </span>
          Informations de la chambre
        </h2>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Nom de la chambre *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Ex : Chambre Standard"
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-700 outline-none focus:border-primary-400"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Prix / nuit (FCFA) *</label>
            <input
              type="number"
              min="0"
              value={form.price_per_night || ''}
              onChange={(e) => update('price_per_night', Number(e.target.value))}
              placeholder="35000"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-700 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Capacité (personnes)</label>
            <input
              type="number"
              min="1"
              value={form.capacity || ''}
              onChange={(e) => update('capacity', Number(e.target.value))}
              placeholder="2"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-700 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Type de lit</label>
            <select
              value={form.bed_type}
              onChange={(e) => update('bed_type', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              {BED_TYPES.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            rows={4}
            maxLength={1000}
            placeholder="Décrivez la chambre : équipements, vue, confort..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-700 outline-none focus:border-primary-400 resize-none"
          />
        </div>
      </section>

      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
            <i className="ri-image-line text-accent-400" />
          </span>
          Photos de la chambre
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {form.images.map((img, idx) => (
            <div key={idx} className="relative rounded-lg overflow-hidden bg-background-50 border border-background-200/70">
              <div className="h-32 w-full overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
              </div>
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-foreground-950/70 text-foreground-50"
                aria-label="Retirer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploading || !businessId}
            className="h-32 rounded-lg border-2 border-dashed border-background-300/60 flex flex-col items-center justify-center gap-2 text-foreground-400 hover:border-primary-400/40 transition-colors disabled:opacity-50"
          >
            <i className="ri-add-line text-xl" />
            <span className="text-xs">{uploading ? 'Envoi...' : 'Ajouter'}</span>
          </button>
        </div>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleImageUpload(e.target.files)}
        />
      </section>

      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
            <i className="ri-checkbox-multiple-line text-secondary-400" />
          </span>
          Équipements
        </h2>
        <div className="flex flex-wrap gap-2">
          {ROOM_AMENITIES.map((amenity) => (
            <button
              type="button"
              key={amenity}
              onClick={() => toggleAmenity(amenity)}
              className={`px-3 py-2 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
                form.amenities.includes(amenity)
                  ? 'bg-primary-500 text-foreground-950 border-primary-500'
                  : 'bg-background-50 text-foreground-500 border-background-300/60 hover:border-primary-400/40'
              }`}
            >
              {amenity}
            </button>
          ))}
        </div>
      </section>

      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.available}
            onChange={(e) => update('available', e.target.checked)}
            className="w-4 h-4 accent-primary-500"
          />
          <span className="text-sm text-foreground-400">Chambre actuellement disponible à la réservation</span>
        </label>
      </section>

      {status === 'error' && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">{errorMsg}</div>
      )}
      {status === 'success' && (
        <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400">
          Chambre enregistrée avec succès.
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || uploading}
          className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? 'Enregistrement...' : existing?.id ? 'Mettre à jour' : 'Ajouter la chambre'}
        </button>
      </div>
    </form>
  );
}

export default RoomForm;